/**
 * Application Configuration
 * 
 * Provides runtime configuration with three layers (in priority order):
 * 1. Runtime overrides (via Config.set() or Config.enableFeature() in browser console)
 * 2. Docker environment variables (injected at container startup via window.__APP_CONFIG__)
 * 3. Default values (hardcoded below)
 * 
/**
 * TypeScript version of the runtime Config manager.
 *
 * Behavior is identical to the original: three-layer config (defaults, Docker-injected
 * `window.__APP_CONFIG__`, and runtime overrides). Exposes `window.Config` for console usage.
 */

type ConfigMap = Record<string, any>;

// Default configuration values (flat key-value)
const DEFAULTS: ConfigMap = {
  basePath: '/',
  listenPort: 3000,
  allowedHosts: ['portaleurope.totalmateria.com'],
  portalNoAuth: 'https://portaleurope.totalmateria.com/en/search/quick',
  portalAuth:
    'https://identity.totalmateria.com/Login/LoginWithSession?sessionId=<sessionId>&lng=<lngCode>&tenantId=<tenantId>',
  ssoUrl:
    'https://sso.totalmateria.com/saml/Auth/Login/?CustomerCode=KTMRENFIELD_NEWINT_NEWOKTA',
  appTitle: 'PS Assignment Wizzard',
  toWindchillOrigin: 'https://dev5.lp-global-plm.abb.com',
  notifyReadyOrigin: '*',
  fromWindchillOrigins: ['https://dev5.lp-global-plm.abb.com', window.location.origin, 'https://windchill-test.prostep.com'],
  fromCreoOrigins: ['http://localhost:3000', 'http://localhost:8080', 'http://localhost', window.location.origin],
  tmportalOrigin: 'https://portaleurope.totalmateria.com',
  //matIdPrefix: '1SAA',
  authSessionDurationMs: 4 * 60 * 60 * 1000  // 8 hours
};

class ConfigManager {
  private _defaults: ConfigMap;
  private _dockerConfig: ConfigMap;
  private _runtimeOverrides: ConfigMap;
  private _listeners: Set<(key: string, value: any) => void>;

  constructor() {
    this._defaults = { ...DEFAULTS };
    this._dockerConfig = (window.__APP_CONFIG__ as ConfigMap) || {};
    this._runtimeOverrides = {};
    this._listeners = new Set();
    // Expose to window for console access (cast to any to avoid duplicate-type issues)
    (window as any).Config = this;
    // eslint-disable-next-line no-console
    console.log('[Config] Initialized with:', this.getAll());
  }

  get(key: string): any {
    if (Object.prototype.hasOwnProperty.call(this._runtimeOverrides, key)) return this._runtimeOverrides[key];
    if (Object.prototype.hasOwnProperty.call(this._dockerConfig, key)) return this._dockerConfig[key];
    return this._defaults[key];
  }

  propertyContains(property: string, value: string): boolean {
    let val = this.get(property);
    if(val instanceof Array){
      return val.includes(value);
    } else if(typeof val === 'string'){
      return val.split(',').map(s => s.trim()).includes(value);
    }
    return false;
  }

  set(key: string, value: any): void {
    this._runtimeOverrides[key] = value;
    // eslint-disable-next-line no-console
    console.log(`[Config] Set '${key}' to:`, value);
    this._notifyChange(key, value);
  }

  getAll(): ConfigMap {
    return {
      ...this._defaults,
      ...this._dockerConfig,
      ...this._runtimeOverrides,
    };
  }

  getOverrides(): ConfigMap {
    return { ...this._runtimeOverrides };
  }

  reset(): void {
    this._runtimeOverrides = {};
    // eslint-disable-next-line no-console
    console.log('[Config] Runtime overrides cleared');
    this._notifyChange('*', null);
  }

  resetKey(key: string): void {
    delete this._runtimeOverrides[key];
    // eslint-disable-next-line no-console
    console.log(`[Config] Reset '${key}' to default/docker value`);
    this._notifyChange(key, this.get(key));
  }

  subscribe(callback: (key: string, newValue: any) => void): () => void {
    this._listeners.add(callback);
    return () => this._listeners.delete(callback);
  }

  private _notifyChange(key: string, value: any): void {
    this._listeners.forEach((cb) => {
      try {
        cb(key, value);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[Config] Listener error:', e);
      }
    });
  }
}

// Expose window typing now that ConfigManager is declared
declare global {
  interface Window {
    __APP_CONFIG__?: ConfigMap;
    Config?: ConfigManager | undefined;
  }
}

const Config = new ConfigManager();
export default Config;

